import Backbone from 'backbone'
import linearGauge from 'linear-gauge'
import formatValue, { shortenedAndUnshortened } from 'scoreboard/components/format-value'
import _ from 'lodash'
import config from 'scoreboard/components/config'
import i18n from 'scoreboard/components/i18n'
import constants from 'scoreboard/components/constants'
import {
	title, subtitle, value, colors,
	thresholds, createNonAbbreviatedTooltip, initValueFormatter
} from './gauge'
import cleanObject from 'scoreboard/components/clean-object'
import ScorecardNode from 'scoreboard/scorecards/scorecard-node'
const { metricish } = ScorecardNode
import calendars from 'scoreboard/components/calendars'
import globals from 'scoreboard/components/globals'
import DataType from 'scoreboard/datasets/model/data-type'
import { DATASETS, INITIATIVES } from '../sections'
const { YES_NO } = DataType

var debug = true

var Linear = Backbone.View.extend({

	className: 'linear-gauge-container',

	initialize: function(options) {
		this.options = options
		// standardize model format as plain object, not backbone model
		this.options.model = options.model.attributes || options.model
		this.thresholdTooltips = []
	},

	render: function(options) {
		var opt = _.extend({}, this.options, options)

		// note: the linear gauge needs this.el in the DOM to support word wrapping in SVGs.
		// scorecards-overview and dashboards/display/display.js both ensure this by
		// adding the element to the DOM before calling render()
		this.data = data(opt)

		if (debug) {
			console.log(JSON.stringify(this.data, null, '    '))
		}

		linearGauge(this.el, this.data, {
			gaugeHeight: opt.gaugeHeight,
			width: this.options.width,
			height: this.options.height,
			fontSize: opt.fontSize
		})

		// Nix any existing non-abbreviated value tooltips
		this._removeTooltips()

		// Create new tooltips for the value/score related parts of the radial gauge
		// if the displayed values were shortened
		if (this.data.value) {
			var { shortened, unshortened } = shortenedAndUnshortened({
				value: this.data.value.raw,
				precision: this.options.model.precision,
				dataType: this.options.model.dataType,
				currencyCode: this.options.model.currencyCode
			})

			if (shortened != unshortened) {
				this.currentValueTooltip = createNonAbbreviatedTooltip(
					this.el,
					'g.current-label',
					'current-label-abbreviated-value ' + this.options.tooltipExtraClass,
					calendars.periodsById[globals.periodId].label,
					unshortened)
			}
		}

		if (this.data.thresholds && this.data.thresholds.length > 0 && !_.isNil(opt.model.scoringTypeId)) {
			var scoringType = config.scoringType(opt.model.scoringTypeId)
			// #21286 careful, opt.currentScore will be null for yes/no metrics
			// that don't have a value for the period
			, 	goal = _.get(opt.currentScore, `thresholds[${scoringType.goalIndex}]`)
			// It's possible there's no goal here (yes/no metrics) #19458
			,	goalKey = goal && scoringType.thresholdNames[scoringType.goalIndex]

			for (var i = 0; i < this.data.thresholds.length; i++) {
				var { shortened, unshortened } = shortenedAndUnshortened({
					value: this.data.thresholds[i],
					precision: this.options.model.precision,
					dataType: this.options.model.dataType,
					currencyCode: this.options.model.currencyCode
				})

				// Assign the correct threshold label to the thresholds along the gauge
				var label
				_.forEach(this.data._thresholds, threshold => {
					if (threshold.y0 == this.data.thresholds[i]) {
						label = threshold.y0 == goal ? i18n.get(goalKey) : threshold.color
					} else if (i == this.data.thresholds.length - 1) {
						label = threshold.y1 == goal ? i18n.get(goalKey) : threshold.color
					}
				})

				if (shortened != unshortened) {
					this.thresholdTooltips.push(createNonAbbreviatedTooltip(
						this.el,
						'text.threshold-label:nth-child(' + (i + 1) + ')',
						'theshold-label-abbreviated-value ' + this.options.tooltipExtraClass,
						label,
						unshortened))
				}
			}
		}

		this.trigger('done')
		return this
	},

	_removeTooltips: function() {
		_.invokeMap([...this.thresholdTooltips, this.currentValueTooltip], 'remove')
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		this._removeTooltips()
	}
})

function data(opt) {
	var opt = _.extend({ score: true, value: true }, opt)
	let rawThresholds = thresholds(opt)

	return cleanObject({
		title: title(opt),
		subtitle: subtitle(opt),
		value: linearValue(opt),
		previous: previous(opt),
		colors: colors(opt),
		thresholds: rawThresholds,
		thresholdLabels: thresholdLabels(rawThresholds, opt),
		fields: fields(opt),
		units: units(opt),
		hoverText: opt.hoverText
	})
}

function linearValue (opt) {
	var val = value(_.extend({showActual: true}, opt))

	if (!val) {
		// Use a placeholder space if labels are turned on, so there is blank space indicating a missing
		// value. See #14971
		// #32140 if label is turned off and there's no score, just return an
		// empty object
		return opt.pointerLabel ? {formatted: ''} : {}
	}

	if (opt.pointerLabel
		&& opt.section === 'scorecards' && !metricish(opt.model)) {

		// Non-metric scorecard items use the score for the pointer
		var { shortened, unshortened } = shortenedAndUnshortened({
			value: val.raw,
			dataType: 'score'
		})
		val.formatted = shortened
		val.unshortened = unshortened
	} else if (!opt.pointerLabel) {
		delete val.formatted
	}

	return val
}

function fields (opt) {
	if (!opt.score) {
		return
	}

	var result = { label: i18n.get('scorecards.node.score') }
	var formatted, scoreUnshortened
	if (opt.currentScore) {
		if (opt.currentScore.applicable) {
			var {shortened, unshortened} = shortenedAndUnshortened({
				value: opt.currentScore.score,
				dataType: 'score'
			})
			formatted = shortened || NO_VALUE
			scoreUnshortened = unshortened || NO_VALUE
		} else {
			formatted = scoreUnshortened = i18n.get('scorecards.node.metric.notapplicable')
		}
	} else {
		formatted = NO_VALUE
	}

	return [{
		label: i18n.get('scorecards.node.score'),
		formatted: formatted,
		unshortened: scoreUnshortened
	}]
}

function formatScore (value) {
	return formatValue({
		value,
		dataType: 'score'
	})
}

function formatMetricSeriesValue (opt, val) {
	return formatValue({
		value: val,
		dataType: opt.model.dataType,
		precision: opt.model.precision,
		currencyCode: opt.model.currencyCode
	})
}

function units (opt) {
	return opt.model.unit
}

function previous (opt) {
	if (!opt.previousScore) {
		return null
	}

	// Reuse the logic for calculating the pointer for the current value by
	// sending in the previous score as the current score
	let previousOpt = _.extend({}, opt, {
		currentScore: opt.previousScore
	})

	let val = linearValue(previousOpt)
	return val?.raw
}

/**
 * Returns a formatter for theshold labels based on the type of linear gauge
 * being displayed
 */
function getThresholdLabelFormatter(opt) {
	if (metricish(opt.model)) {
		return _.bind(formatMetricSeriesValue, null, opt)
	} else if (opt.section === INITIATIVES) {
		return initValueFormatter(opt)
	} else if (opt.section === DATASETS) {
		return value => formatValue({
			value,
			dataType: opt.dataType,
			precision: opt.precision,
			currencyCode: opt.currencyCode
		})
	} else {
		return formatScore
	}
}

function thresholdLabels (rawThresholds, opt) {
	if (!opt.thresholds) {
		return
	}
	var formatter = getThresholdLabelFormatter(opt)
	var formatted = _.map(rawThresholds, formatter)

	if (metricish(opt.model)) {
		let scoringType = config.scoringType(opt.model.scoringTypeId)
		if (opt.model.dataType === YES_NO) {
			// For yes/no metrics, we just want No at one end and Yes at the other end,
			// so get rid of the label for the middle
			formatted[1] = ''
		} else if (_.get(scoringType, 'standardScoringType') == constants.GOAL_ONLY) {
			let goal = _.get(opt, 'currentScore.thresholds[0]')

			if (_.isNil(goal)) {
				// #20382 if there is no threshold entered for this period, but we
				// are supposed to be showing it, just return an empty threshold
				// so the empty space will be rendered instead of filled
				formatted = EMPTY_THRESHOLDS
			} else {
				// #19311 on goal-only metrics, we make fake thresholds to give proper
				// padding, but make sure we just show the real goal threshold in the middle.
				// Need to use non-breaking-space on left/right thresholds or it messes up
				// scoreboard-linear-gauge's height calculations
				formatted[0] = formatted[2] = EMPTY_SPACE
				formatted[1] = formatter(goal)
			}
		}
	}

	if (opt.thresholds && _.isEmpty(formatted)) {
		// #20209 if there are no thresholds entered for this period, but we
		// are supposed to be showing them, just return a non-breaking-space
		// threshold so that scoreboard-linear-gauge will render the empty space
		// instead of expanding the gauge to fill the space
		formatted = EMPTY_THRESHOLDS
	}

	return formatted
}

var NO_VALUE = '-'
const EMPTY_SPACE = '\xa0'
const EMPTY_THRESHOLDS = ['\xa0']

_.extend(Linear, {
	data
})
export default Linear
export { data }

Linear.__filename__ = 'components/gauge/linear.js'
